import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { PersonType, initialPersonType, finalizePersonTypeForSave, getBackofficeDefaultLanguage } from '../models/personType'
import {
  createPersonType,
  deletePersonType,
  getPersonTypes,
  updatePersonType,
  getPersonType,
} from '../api/personTypesApi'
import { KTSVG } from '../../../../../_dashboard/helpers/components/KTSVG'
import { toast } from 'react-toastify'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'

const LANGUAGES = [
  { code: 'en', labelKey: 'TOUR_PERSON_TYPE.TAB_LANG_EN' },
  { code: 'fr', labelKey: 'TOUR_PERSON_TYPE.TAB_LANG_FR' },
  { code: 'de', labelKey: 'TOUR_PERSON_TYPE.TAB_LANG_DE' },
]

/** Onglet aligné sur la langue du BO (StickyLanguageSelector) si elle existe dans le formulaire. */
function resolvePersonTypeTabLang(code: string): string {
  const lc = code.trim().toLowerCase()
  return LANGUAGES.some((l) => l.code === lc) ? lc : 'en'
}

function clonePersonTypeValues(base: PersonType): PersonType {
  return {
    ...base,
    translations: LANGUAGES.map(({ code }) => {
      const found = base.translations?.find(
        (t) => String(t.language ?? '').trim().toLowerCase() === code.toLowerCase(),
      )
      return {
        language: code,
        name: found?.name ?? '',
        description: found?.description ?? '',
      }
    }),
  }
}

const PersonTypesTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [personTypes, setPersonTypes] = useState<PersonType[]>([])
  const [editingPersonType, setEditingPersonType] = useState<PersonType | null>(null)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [personTypeToDelete, setPersonTypeToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [activeLangTab, setActiveLangTab] = useState(() =>
    resolvePersonTypeTabLang(getBackofficeDefaultLanguage()),
  )
  const pageSize = 1000

  const loadPersonTypes = async () => {
    try {
      setLoading(true)
      const response = await getPersonTypes(page, pageSize)
      setPersonTypes(response.data || [])
      setTotalPages(response.payload.pagination.totalPages || 0)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPersonTypes()
  }, [page])

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!showModal && !showDeleteConfirm) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.body.classList.add('modal-open')
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.classList.remove('modal-open')
    }
  }, [showModal, showDeleteConfirm])

  const personTypeSchema = useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string()
          .transform((raw) =>
            String(raw ?? '')
              .replace(/[^A-Za-z_]/g, '')
              .toUpperCase(),
          )
          .required(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.CODE_REQUIRED' }))
          .matches(
            /^[A-Z_]+$/,
            intl.formatMessage({
              id: 'TOUR_PERSON_TYPE.VALIDATION.CODE_PATTERN',
              defaultMessage:
                'Le code ne peut contenir que des lettres A-Z et des underscores (ex: ADULTE)',
            }),
          ),
        defaultPriceMultiplier: Yup.number()
          .required(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.PRICE_MULTIPLIER_REQUIRED' }))
          .min(0, intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.PRICE_MULTIPLIER_MIN' }))
          .max(10, intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.PRICE_MULTIPLIER_MAX' })),
        minAge: Yup.number()
          .required(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.MIN_AGE_REQUIRED' }))
          .min(0, intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.MIN_AGE_MIN' }))
          .max(Yup.ref('maxAge'), intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.MIN_AGE_MAX' })),
        maxAge: Yup.number()
          .required(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.MAX_AGE_REQUIRED' }))
          .min(Yup.ref('minAge'), intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.MAX_AGE_MIN' })),
        isActive: Yup.boolean().required(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.STATUS_REQUIRED' })),
        translations: Yup.array()
          .of(
            Yup.object().shape({
              language: Yup.string(),
              name: Yup.string(),
              description: Yup.string(),
            })
          )
          .test(
            'at-least-one-name',
            intl.formatMessage({ id: 'TOUR_PERSON_TYPE.VALIDATION.AT_LEAST_ONE_TRANSLATION' }),
            (translations) => {
              return !!translations?.some((t) => t?.name?.trim())
            }
          ),
      }),
    [intl]
  )

  const formik = useFormik({
    initialValues: editingPersonType || initialPersonType,
    validationSchema: personTypeSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = finalizePersonTypeForSave(values)
        if (editingPersonType?._id) {
          await updatePersonType(editingPersonType._id, payload)
          toast.success(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.SUCCESS.UPDATE' }))
        } else {
          await createPersonType(payload)
          toast.success(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.SUCCESS.CREATE' }))
        }
        resetForm()
        setEditingPersonType(null)
        setShowModal(false)
        loadPersonTypes()
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: editingPersonType?._id
              ? 'TOUR_PERSON_TYPE.ERROR.UPDATE'
              : 'TOUR_PERSON_TYPE.ERROR.CREATE',
          })
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDelete = async () => {
    if (!personTypeToDelete) return

    try {
      setIsDeleting(true)
      await deletePersonType(personTypeToDelete)
      toast.success(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.SUCCESS.DELETE' }))
      loadPersonTypes()
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.ERROR.DELETE' }))
    } finally {
      setIsDeleting(false)
      setShowDeleteConfirm(false)
      setPersonTypeToDelete(null)
    }
  }

  const handleEdit = async (personType: PersonType) => {
    try {
      setLoading(true)
      const response = await getPersonType(personType._id!)
      const data = response.data as PersonType
      const translations = LANGUAGES.map(({ code }) => {
        const existing = data.translations?.find((t: { language: string }) => t.language === code)
        return existing
          ? { language: code, name: existing.name ?? '', description: existing.description ?? '' }
          : { language: code, name: '', description: '' }
      })
      const normalized = clonePersonTypeValues({ ...data, translations })
      setEditingPersonType(normalized)
      formik.setValues(normalized)
      setActiveLangTab(resolvePersonTypeTabLang(getBackofficeDefaultLanguage()))
      setShowModal(true)
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'TOUR_PERSON_TYPE.ERROR.LOAD' }))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setEditingPersonType(null)
    formik.resetForm({ values: clonePersonTypeValues(initialPersonType) })
    setShowModal(false)
  }

  const handleAddNew = () => {
    setEditingPersonType(null)
    formik.resetForm({ values: clonePersonTypeValues(initialPersonType) })
    setActiveLangTab(resolvePersonTypeTabLang(getBackofficeDefaultLanguage()))
    setShowModal(true)
  }

  const renderLangFields = () => {
    const lang = activeLangTab.trim().toLowerCase()
    /** Toujours 4 lignes (en/fr/es/de), même si le state Formik était partiel. */
    const translations = clonePersonTypeValues({
      ...formik.values,
      translations: formik.values.translations?.length ? formik.values.translations : undefined,
    } as PersonType).translations!

    const tr =
      translations.find((t) => String(t.language).toLowerCase() === lang) ?? {
        language: lang,
        name: '',
        description: '',
      }

    const patchTranslation = (field: 'name' | 'description', value: string) => {
      const next = translations.map((row) =>
        String(row.language).toLowerCase() === lang ? { ...row, [field]: value } : row,
      )
      void formik.setFieldValue('translations', next, true)
      void formik.setFieldTouched('translations', true, false)
    }

    return (
      <div className='person-type-lang-panel'>
        <div className='mb-4'>
          <label className='form-label' htmlFor={`person-type-name-${lang}`}>
            {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.NAME' })}
          </label>
          <input
            id={`person-type-name-${lang}`}
            name={`translations.${lang}.name`}
            type='text'
            autoComplete='off'
            className='person-type-lang-input'
            placeholder={intl.formatMessage({ id: 'TOUR_PERSON_TYPE.NAME_PLACEHOLDER' })}
            value={tr.name ?? ''}
            onChange={(e) => patchTranslation('name', e.target.value)}
          />
        </div>
        <div>
          <label className='form-label' htmlFor={`person-type-desc-${lang}`}>
            {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.DESCRIPTION' })}
          </label>
          <textarea
            id={`person-type-desc-${lang}`}
            name={`translations.${lang}.description`}
            className='person-type-lang-input'
            rows={4}
            placeholder={intl.formatMessage({ id: 'TOUR_PERSON_TYPE.DESCRIPTION_PLACEHOLDER' })}
            value={tr.description ?? ''}
            onChange={(e) => patchTranslation('description', e.target.value)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='card card-custom'>
      {/* Header with Add Button */}
      <div className='card-header border-0 pt-6'>
        <div className='card-title'></div>
        <div className='card-toolbar'>
          <div className='d-flex justify-content-end'>
            <button type='button' className='btn btn-primary' onClick={handleAddNew}>
              <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
              {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.ADD_NEW' })}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className='card-body pt-0'>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center min-h-300px'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed fs-6 gy-5'>
                <thead>
                  <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                    <th>{intl.formatMessage({ id: 'TOUR_PERSON_TYPE.NAME' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_PERSON_TYPE.CODE' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_PERSON_TYPE.PRICE_MULTIPLIER' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_PERSON_TYPE.AGE_RANGE' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_PERSON_TYPE.STATUS' })}</th>
                    <th>{intl.formatMessage({ id: 'TOUR_PERSON_TYPE.ACTIONS' })}</th>
                  </tr>
                </thead>
                <tbody className='text-gray-600 fw-semibold'>
                  {personTypes.map((personType) => (
                    <tr key={personType._id}>
                      <td>{(personType.translations?.[0]?.name) || personType.name || personType.code}</td>
                      <td>{personType.code}</td>
                      <td>{personType.defaultPriceMultiplier}</td>
                      <td>{`${personType.minAge} - ${personType.maxAge === 999 ? '∞' : personType.maxAge}`}</td>
                      <td>
                        <span
                          className={`badge badge-light-${
                            personType.isActive ? 'success' : 'danger'
                          } fw-bold`}
                        >
                          {intl.formatMessage({ id: personType.isActive ? 'STATUS.ACTIVE' : 'STATUS.INACTIVE' })}
                        </span>
                      </td>
                      <td>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({ id: 'ACTION.EDIT' })}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-primary btn-sm me-1'
                            onClick={() => handleEdit(personType)}
                          >
                            <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip>{intl.formatMessage({ id: 'ACTION.DELETE' })}</Tooltip>}
                        >
                          <button
                            className='btn btn-icon btn-light-danger btn-sm'
                            onClick={() => {
                              if (personType._id) {
                                setPersonTypeToDelete(personType._id)
                                setShowDeleteConfirm(true)
                              }
                            }}
                          >
                            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className='d-flex justify-content-between align-items-center flex-wrap'>
                <div className='d-flex flex-wrap py-2 mr-3'>
                  <button
                    className='btn btn-light btn-active-light-primary me-2'
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.PREVIOUS' })}
                  </button>
                  <button
                    className='btn btn-light btn-active-light-primary'
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {intl.formatMessage({ id: 'PAGINATION.NEXT' })}
                  </button>
                </div>
                <div className='d-flex align-items-center py-3'>
                  <span className='text-muted'>
                    {intl.formatMessage(
                      { id: 'PAGINATION.INFO' },
                      {
                        page: page + 1,
                        total: totalPages,
                      }
                    )}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal — portail body : évite overflow/transform parent (carte config) qui masque les champs */}
      {showModal &&
        createPortal(
          <div
            className='modal fade show d-block'
            role='dialog'
            aria-modal='true'
            aria-labelledby='person-type-modal-title'
            tabIndex={-1}
            style={{
              zIndex: 12050,
              backgroundColor: 'rgba(15, 23, 42, 0.45)',
            }}
          >
            <div className='modal-dialog modal-dialog-centered mw-650px'>
              <div className='modal-content border-0 rounded-4 shadow-lg'>
                <div className='modal-header border-0 pt-6 px-8 pb-2'>
                  <h5 className='modal-title fs-3 fw-bold text-gray-900' id='person-type-modal-title'>
                    {editingPersonType
                      ? intl.formatMessage({ id: 'TOUR_PERSON_TYPE.EDIT' })
                      : intl.formatMessage({ id: 'TOUR_PERSON_TYPE.ADD_NEW' })}
                  </h5>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-active-color-primary'
                    onClick={handleCancel}
                    disabled={formik.isSubmitting}
                  >
                    <i className='bi bi-x-lg fs-3' />
                  </button>
                </div>
                <form onSubmit={formik.handleSubmit} noValidate>
                  <div className='modal-body px-8 py-6'>
                  <div className='row mb-6'>
                    <div className='col-lg-12'>
                      <label className='form-label mb-2'>
                        {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.NAME' })} /{' '}
                        {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.DESCRIPTION' })}
                      </label>
                      <div className='d-flex flex-wrap gap-2 pb-2 border-bottom mb-3'>
                        {LANGUAGES.map(({ code, labelKey }) => (
                          <button
                            key={code}
                            type='button'
                            className={`btn btn-sm fw-semibold ${
                              activeLangTab === code ? 'btn-primary' : 'btn-light btn-active-light-primary border'
                            }`}
                            aria-pressed={activeLangTab === code}
                            onClick={() => setActiveLangTab(code)}
                          >
                            {intl.formatMessage({ id: labelKey })}
                          </button>
                        ))}
                      </div>
                      {renderLangFields()}
                      {formik.touched.translations && formik.errors.translations && (
                        <div className='invalid-feedback d-block mt-2'>{formik.errors.translations}</div>
                      )}
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-12'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.CODE' })}
                        </label>
                        <input
                          type='text'
                          className={`form-control ${
                            formik.touched.code && formik.errors.code ? 'is-invalid' : ''
                          }`}
                          placeholder={intl.formatMessage({ id: 'TOUR_PERSON_TYPE.CODE_PLACEHOLDER' })}
                          {...formik.getFieldProps('code')}
                        />
                        {formik.touched.code && formik.errors.code && (
                          <div className='invalid-feedback'>{formik.errors.code}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-4'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.PRICE_MULTIPLIER' })}
                        </label>
                        <input
                          type='number'
                          step='0.1'
                          className={`form-control ${
                            formik.touched.defaultPriceMultiplier && formik.errors.defaultPriceMultiplier
                              ? 'is-invalid'
                              : ''
                          }`}
                          {...formik.getFieldProps('defaultPriceMultiplier')}
                        />
                        {formik.touched.defaultPriceMultiplier && formik.errors.defaultPriceMultiplier && (
                          <div className='invalid-feedback'>{formik.errors.defaultPriceMultiplier}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-4'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.MIN_AGE' })}
                        </label>
                        <input
                          type='number'
                          className={`form-control ${
                            formik.touched.minAge && formik.errors.minAge ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('minAge')}
                        />
                        {formik.touched.minAge && formik.errors.minAge && (
                          <div className='invalid-feedback'>{formik.errors.minAge}</div>
                        )}
                      </div>
                    </div>

                    <div className='col-lg-4'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.MAX_AGE' })}
                        </label>
                        <input
                          type='number'
                          className={`form-control ${
                            formik.touched.maxAge && formik.errors.maxAge ? 'is-invalid' : ''
                          }`}
                          {...formik.getFieldProps('maxAge')}
                        />
                        {formik.touched.maxAge && formik.errors.maxAge && (
                          <div className='invalid-feedback'>{formik.errors.maxAge}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='row mb-6'>
                    <div className='col-lg-6'>
                      <div className='form-group'>
                        <label className='form-label required'>
                          {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.STATUS' })}
                        </label>
                        <div className='form-check form-switch form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            checked={formik.values.isActive}
                            onChange={(e) => {
                              formik.setFieldValue('isActive', e.target.checked)
                            }}
                            id='statusSwitch'
                          />
                          <label className='form-check-label' htmlFor='statusSwitch'>
                            {intl.formatMessage({
                              id: formik.values.isActive ? 'STATUS.ACTIVE' : 'STATUS.INACTIVE',
                            })}
                          </label>
                        </div>
                        {formik.touched.isActive && formik.errors.isActive && (
                          <div className='invalid-feedback d-block'>{formik.errors.isActive}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                  <button
                    type='button'
                    className='btn btn-light-primary fw-semibold px-6'
                    onClick={handleCancel}
                    disabled={formik.isSubmitting}
                  >
                    {intl.formatMessage({ id: 'ACTION.CANCEL' })}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary fw-semibold px-8'
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                        <span className='ms-2'>{intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}</span>
                      </>
                    ) : (
                      intl.formatMessage({ id: editingPersonType ? 'ACTION.UPDATE' : 'ACTION.SAVE' })
                    )}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm &&
        createPortal(
          <div
            className='modal fade show d-block'
            role='dialog'
            aria-modal='true'
            tabIndex={-1}
            style={{
              zIndex: 12100,
              backgroundColor: 'rgba(15, 23, 42, 0.45)',
            }}
          >
            <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content border-0 rounded-4 shadow-lg'>
              <div className='modal-header border-0 pt-6 px-8 pb-2'>
                <h5 className='modal-title fs-3 fw-bold text-gray-900'>
                  {intl.formatMessage({ id: 'HEADER.DELETE' })}
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setPersonTypeToDelete(null)
                  }}
                  disabled={isDeleting}
                >
                  <i className='bi bi-x-lg fs-3' />
                </button>
              </div>
              <div className='modal-body px-8 py-6'>
                {intl.formatMessage({ id: 'TOUR_PERSON_TYPE.DELETE_CONFIRM' })}
              </div>
              <div className='modal-footer border-0 pt-0 pb-7 px-8 d-flex justify-content-end gap-3'>
                <button
                  type='button'
                  className='btn btn-light-primary fw-semibold px-6'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setPersonTypeToDelete(null)
                  }}
                  disabled={isDeleting}
                >
                  {intl.formatMessage({ id: 'ACTION.CANCEL' })}
                </button>
                <button
                  type='button'
                  className='btn btn-danger fw-semibold px-6'
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                      <span className='ms-2'>{intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}</span>
                    </>
                  ) : (
                    intl.formatMessage({ id: 'ACTION.DELETE' })
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

export default PersonTypesTab 